import type { PropsWithChildren, ReactElement } from "react";

import { translate } from "app/filters/i18nFilter";
import { FormSelectCombobox } from "app/pages/sales-intelligence/common-components/dropdown/FormSelectCombobox";
import type { IFormSelectBaseProps } from "app/pages/sales-intelligence/common-components/form-fields";
import { DROPDOWN_WIDTH } from "app/pages/sales-intelligence/common-components/form-fields/components/FormSelect/styles";
import type { FormSelectValue } from "app/pages/sales-intelligence/common-components/form-fields/components/FormSelect/types";

import { COLUMN_GAP, StyledRangeSelectContent, StyledRangeSelectText } from "./styles";

const MIN_SELECT_ID = "min";
const MAX_SELECT_ID = "max";

export interface IRangeSelectProps<V extends FormSelectValue> extends IFormSelectBaseProps<V> {
    value: readonly (V | undefined)[];
    optionsMin: readonly V[];
    optionsMax?: readonly V[];
    placeholderMin?: string;
    placeholderMax?: string;
    width?: number;
    columnGap?: number;
    onChange: (value: (V | undefined)[]) => void;
    isDisabled?: boolean;
    isTopLayer?: boolean;
}

export const RangeSelect = <V extends FormSelectValue>({
    value,
    optionsMin,
    optionsMax,
    placeholderMin,
    placeholderMax,
    fieldName,
    formatValue,
    onChange,
    children,
    width = DROPDOWN_WIDTH,
    columnGap = COLUMN_GAP,
    isDisabled = false,
    isTopLayer,
}: PropsWithChildren<IRangeSelectProps<V>>): ReactElement => {
    const [currentMinValue, currentMaxValue] = value;

    const handleSelectMinValue = (minValue: V): void => {
        onChange([minValue, currentMaxValue]);
    };

    const handleSelectMaxValue = (maxValue: V): void => {
        onChange([currentMinValue, maxValue]);
    };

    return (
        <StyledRangeSelectContent columnGap={columnGap}>
            <FormSelectCombobox<V>
                fieldName={`${fieldName}-${MIN_SELECT_ID}`}
                value={currentMinValue}
                options={optionsMin}
                placeholder={placeholderMin}
                formatValue={formatValue}
                isTopLayer={isTopLayer}
                onChange={handleSelectMinValue}
                width={width}
                isDisabled={isDisabled}
            />

            <StyledRangeSelectText>{translate("common.to")}</StyledRangeSelectText>

            <FormSelectCombobox<V>
                fieldName={`${fieldName}-${MAX_SELECT_ID}`}
                value={currentMaxValue}
                options={optionsMax || optionsMin}
                placeholder={placeholderMax}
                formatValue={formatValue}
                isTopLayer={isTopLayer}
                onChange={handleSelectMaxValue}
                width={width}
                isDisabled={isDisabled}
            />

            {children}
        </StyledRangeSelectContent>
    );
};
